import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Trash2, ShieldAlert, Users, Calendar, MessageSquare, Briefcase, Clock,
  AlertTriangle, CheckCircle2, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATS = [
  { key: 'test_customers', label: 'Test Customers',  icon: Users,         color: '#6366F1' },
  { key: 'test_bookings',  label: 'Test Bookings',   icon: Calendar,      color: '#FF3B6B' },
  { key: 'test_messages',  label: 'Test Messages',   icon: MessageSquare, color: '#06D6A0' },
  { key: 'test_employees', label: 'Test Employees',  icon: Briefcase,     color: '#FF7A2E' },
];

export const CrmSettings = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState('soft');
  const [permanentAck, setPermanentAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/test-data/preview`, { withCredentials: true });
      setPreview(res.data);
    } catch {
      toast.error('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const loadAudits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-logs?action=test_data_purge&limit=10`, { withCredentials: true });
      setAudits(res.data?.entries || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadPreview(); loadAudits(); }, []);

  const resetDialog = () => {
    setPin(''); setMode('soft'); setPermanentAck(false);
  };

  const runPurge = async () => {
    if (mode === 'hard' && !permanentAck) {
      toast.error('Please confirm you understand this is permanent');
      return;
    }
    if (!pin || pin.length < 4) {
      toast.error('Enter your PIN or admin password');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/test-data/purge`,
        { pin, mode },
        { withCredentials: true },
      );
      const c = res.data.counts || {};
      toast.success(
        `${mode === 'hard' ? 'Permanently deleted' : 'Soft-deleted'} ${c.bookings || 0} bookings, ${c.messages || 0} messages, ${c.employees || 0} employees`
      );
      setDialogOpen(false);
      resetDialog();
      await Promise.all([loadPreview(), loadAudits()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setBusy(false);
    }
  };

  const total = preview ? (preview.total || 0) : 0;

  return (
    <div data-testid="crm-settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Settings
        </h1>
        <p className="text-[#6B6B7A]">Data management and destructive actions live here.</p>
      </div>

      {/* Data Management section */}
      <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6 mb-6" data-testid="data-management-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Data Management
            </h2>
            <p className="text-sm text-[#6B6B7A] mt-0.5">
              Remove all records that look like test data — customers, bookings, messages, employees — in one audited action.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => { resetDialog(); setDialogOpen(true); }}
            disabled={loading || total === 0}
            className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full h-10 px-5 disabled:opacity-50"
            data-testid="open-purge-dialog-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Remove All Test Data
          </Button>
        </div>

        {/* Preview counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {CATS.map(c => {
            const Icon = c.icon;
            const v = preview?.[c.key] || 0;
            return (
              <div
                key={c.key}
                className="rounded-xl border border-[#E8E5DF] p-4 flex items-center gap-3 bg-[#FFFDF8]"
                data-testid={`count-${c.key}`}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${c.color}15` }}>
                  <Icon className="w-4 h-4" style={{ color: c.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{v}</p>
                  <p className="text-xs text-[#6B6B7A]">{c.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && total === 0 && (
          <div className="mt-5 rounded-xl p-4 bg-[#EDFFF8] border border-[#06D6A0]/30 inline-flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-[#06D6A0]" />
            <span className="text-[#05a07a] font-medium">No test data detected. Database is production-clean.</span>
          </div>
        )}
      </section>

      {/* Recent audit logs */}
      <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="audit-log-section">
        <h3 className="text-base font-semibold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Recent purges
        </h3>
        {audits.length === 0 ? (
          <p className="text-sm text-[#6B6B7A] italic">No purge events yet.</p>
        ) : (
          <ul className="divide-y divide-[#E8E5DF]">
            {audits.map(a => (
              <li key={a.audit_id} className="py-2.5 flex items-center justify-between gap-3 text-sm flex-wrap" data-testid={`audit-row-${a.audit_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="w-3.5 h-3.5 text-[#6B6B7A]" />
                  <span className="text-[#6B6B7A] text-xs">
                    {new Date(a.created_at).toLocaleString()}
                  </span>
                  <span className="font-medium text-[#1E1E24] truncate">{a.admin_name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${a.mode === 'hard' ? 'bg-[#FFE4E1] text-[#D1344D]' : 'bg-[#FFF4D6] text-[#A8780F]'}`}>
                    {a.mode === 'hard' ? 'Permanent' : 'Soft'}
                  </span>
                </div>
                <span className="text-xs text-[#6B6B7A]">
                  {a.counts?.bookings || 0} bookings · {a.counts?.messages || 0} messages · {a.counts?.employees || 0} employees
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Confirmation dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!busy) { setDialogOpen(v); if (!v) resetDialog(); } }}>
        <DialogContent className="bg-white max-w-lg p-0 overflow-hidden" data-testid="purge-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E1E24]/10">
            <DialogTitle className="text-xl font-semibold text-[#FF3B6B] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <ShieldAlert className="w-5 h-5" />
              Remove All Test Data
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">
              This scans the database for records whose email, name, subject or body contains &quot;test&quot;, plus anything explicitly flagged <code>is_test: true</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4">
            <div className="rounded-xl bg-[#FFF8E7] border border-[#FFD166]/40 p-3 text-sm">
              <p className="font-semibold text-[#1E1E24] mb-1.5">This will remove:</p>
              <ul className="text-[#1E1E24] space-y-0.5 text-xs">
                <li>🧑 <b>{preview?.test_customers || 0}</b> test customers</li>
                <li>📅 <b>{preview?.test_bookings || 0}</b> test bookings (+ their allergy forms, add-ons, transactions)</li>
                <li>💬 <b>{preview?.test_messages || 0}</b> test messages</li>
                <li>👥 <b>{preview?.test_employees || 0}</b> test employees (+ their shifts + time entries)</li>
              </ul>
            </div>

            {/* Mode */}
            <div>
              <Label className="text-xs font-semibold text-[#1E1E24]">Deletion mode</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setMode('soft')}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${mode === 'soft' ? 'border-[#06D6A0] bg-[#EDFFF8]' : 'border-[#E8E5DF] bg-white hover:border-[#06D6A0]/40'}`}
                  data-testid="mode-soft-btn"
                >
                  <div className="flex items-center gap-1.5">
                    <RotateCcw className="w-4 h-4 text-[#06D6A0]" />
                    <p className="font-semibold text-sm text-[#1E1E24]">Soft delete</p>
                  </div>
                  <p className="text-[11px] text-[#6B6B7A] mt-0.5">Recoverable · adds deleted_at timestamp</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('hard')}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${mode === 'hard' ? 'border-[#FF3B6B] bg-[#FFF0F3]' : 'border-[#E8E5DF] bg-white hover:border-[#FF3B6B]/40'}`}
                  data-testid="mode-hard-btn"
                >
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-[#FF3B6B]" />
                    <p className="font-semibold text-sm text-[#1E1E24]">Permanent</p>
                  </div>
                  <p className="text-[11px] text-[#6B6B7A] mt-0.5">Cannot be undone</p>
                </button>
              </div>
            </div>

            {mode === 'hard' && (
              <label className="flex items-start gap-2 text-xs text-[#1E1E24] cursor-pointer">
                <Checkbox
                  checked={permanentAck}
                  onCheckedChange={(v) => setPermanentAck(!!v)}
                  className="mt-0.5"
                  data-testid="permanent-ack-checkbox"
                />
                <span>I understand this is <b>permanent</b> and cannot be undone.</span>
              </label>
            )}

            {/* PIN input */}
            <div>
              <Label htmlFor="purge-pin" className="text-xs font-semibold text-[#1E1E24]">
                Confirm with your 4-digit PIN or admin password
              </Label>
              <Input
                id="purge-pin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="•••• or password"
                className="rounded-md border-[#E8E5DF] mt-1.5"
                data-testid="purge-pin-input"
                autoComplete="current-password"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[#1E1E24]/10 flex items-center justify-end gap-2 bg-[#FFFDF8]">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setDialogOpen(false); resetDialog(); }}
              disabled={busy}
              className="text-[#6B6B7A] hover:text-[#1E1E24]"
              data-testid="purge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={runPurge}
              disabled={busy || !pin || (mode === 'hard' && !permanentAck)}
              className={`rounded-full text-white h-10 px-5 disabled:opacity-50 ${mode === 'hard' ? 'bg-[#FF3B6B] hover:bg-[#E62E58]' : 'bg-[#6366F1] hover:bg-[#4f52d4]'}`}
              data-testid="purge-confirm-btn"
            >
              {busy ? 'Removing…' : mode === 'hard' ? 'Permanently Delete All' : 'Soft-delete All'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmSettings;
